import * as helios from "@hyperionbt/helios";
import {
  CardanoNetwork,
  KreateUTxO,
} from "kreate-common/modules/business-types";
import { CLIENT_ENV } from "kreate-env/client";

import ScriptMarketplace from "../../onchain/scriptMarketplace.hl";
import {
  addHeliosCollateral,
  getCardanoNetworkParams,
  toHeliosUTxO,
} from "../../utils";

// Primary
export async function buildTx$BuyArtwork(
  network: CardanoNetwork,
  wallet: helios.Cip30Wallet,
  utxoProtocolParams: KreateUTxO,
  utxoListing: KreateUTxO,
  buyNumber: number,
  txMessage: string
) {
  if (utxoProtocolParams.datum.type !== "protocol-params") {
    throw new Error("wrong datum type for protocol params UTxO");
  }
  if (utxoListing.datum.type !== "marketplace") {
    throw new Error("wrong datum type for listing UTxO");
  }
  if (buyNumber <= 0) {
    throw new Error("Must purchase some Editions");
  }

  const walletHelper = new helios.WalletHelper(wallet);

  const address = await walletHelper.baseAddress;

  const listingHeliosUTxO = toHeliosUTxO(utxoListing);
  const listingDatum = utxoListing.datum.value;

  const sellerAddress = new helios.Address(listingDatum.sellerAddress);
  const isSeller = address.pubKeyHash.eq(sellerAddress.pubKeyHash);

  const tx = new helios.Tx();
  await addHeliosCollateral(wallet, tx);

  tx.addInput(listingHeliosUTxO, new helios.ConstrData(1, []));
  const scriptMarketplace = new ScriptMarketplace().compile(true);
  tx.attachScript(scriptMarketplace);
  tx.addOutput(
    new helios.TxOutput(
      listingHeliosUTxO.origOutput.address,
      helios.Value.fromCbor(listingHeliosUTxO.origOutput.value.toCbor()).sub(
        new helios.Value(
          BigInt(0),
          new helios.Assets([
            [
              listingDatum.assetId.substring(0, 56),
              [[listingDatum.assetId.substring(56), buyNumber]],
            ],
          ])
        )
      ),
      listingHeliosUTxO.origOutput.datum
    )
  );

  /** NOTE: @thanh-kreate
   * Since helios api does not automatically break a string that is less than 64
   * characters to array, this should be done manually to satisfy CIP-20 constraints
   * More details: https://cips.cardano.org/cips/cip20/
   * */
  tx.addMetadata(674, {
    map: [
      [
        "msg",
        helios.textToBytes(txMessage).length <= 64 ? [txMessage] : txMessage,
      ],
    ],
  });

  let protocolFee = 0;
  let buyFee = 0;
  if (isSeller) {
    protocolFee = utxoProtocolParams.datum.value.kreatorMintFee * buyNumber;
  } else {
    buyFee = listingDatum.editionPrice * buyNumber;
    protocolFee = (buyFee * CLIENT_ENV.KOLLECTOR_MINT_FEE_PERCENTAGE) / 100;
  }
  protocolFee = Math.max(protocolFee, CLIENT_ENV.MIN_PROTOCOL_FEE);

  tx.addOutput(
    new helios.TxOutput(
      new helios.Address(utxoProtocolParams.datum.value.treasuryAddress),
      new helios.Value(BigInt(protocolFee))
    )
  );

  if (buyFee) {
    tx.addOutput(
      new helios.TxOutput(sellerAddress, new helios.Value(BigInt(buyFee)))
    );
  }

  // TODO: Improve coin selection here
  // Especially for burning
  const [utxos, remainingUtxos] = await walletHelper.pickUtxos(
    // Ad-hoc padding...
    new helios.Value(BigInt(protocolFee + buyFee + 2_000_000)),
    helios.CoinSelection.selectLargestFirst
  );
  tx.addInputs(utxos);

  if (isSeller) {
    tx.addSigner(sellerAddress.pubKeyHash);
  }

  await tx.finalize(getCardanoNetworkParams(network), address, remainingUtxos);

  const txFee = Number(tx.body.fee);

  return { tx, protocolFee, buyFee, txFee, isSeller };
}
